import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { logError } from "../utils/logger";

const CHANNEL_NAME = "query-cache-invalidation";

/**
 * Hook for cross-tab query cache invalidation using BroadcastChannel.
 *
 * Use this hook in:
 * - Components that DISPLAY data: Sets up a listener to receive invalidation events
 * - Components that MODIFY data: Returns broadcastInvalidation function to notify other tabs
 *
 * @param queryKey - The React Query key to sync across tabs (e.g., ["getNtpThemeFileMetadata", siteId])
 * @param listen - If true, sets up a listener for this query key (default: false)
 */
export function useQueryInvalidationBroadcast(
  queryKey: unknown[],
  listen: boolean = false,
): {
  broadcastInvalidation: () => void;
} {
  const queryClient = useQueryClient();
  const queryKeyRef = useRef(queryKey);
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Keep queryKey ref updated
  useEffect(() => {
    queryKeyRef.current = queryKey;
  }, [queryKey]);

  // Set up listener for invalidation messages from other tabs
  useEffect(() => {
    if (!listen) {
      return;
    }

    try {
      channelRef.current = new BroadcastChannel(CHANNEL_NAME);
    } catch (error) {
      logError({
        error,
        impact: "Failed to create BroadcastChannel for query invalidation",
        extra: { channelName: CHANNEL_NAME },
      });
      return;
    }

    const handleMessage = (event: MessageEvent): void => {
      if (event.data.type === "INVALIDATE_QUERY" && Array.isArray(event.data.queryKey)) {
        // Check if this message is for our query key
        const messageKey = JSON.stringify(event.data.queryKey);
        const ourKey = JSON.stringify(queryKeyRef.current);

        if (messageKey === ourKey) {
          queryClient.invalidateQueries({ queryKey: event.data.queryKey }).catch((error) => {
            logError({
              error,
              impact: "Failed to invalidate query cache from broadcast",
              extra: { queryKey: event.data.queryKey },
            });
          });
        }
      }
    };

    channelRef.current.addEventListener("message", handleMessage);

    return (): void => {
      if (channelRef.current) {
        channelRef.current.removeEventListener("message", handleMessage);
        channelRef.current.close();
        channelRef.current = null;
      }
    };
  }, [listen, queryClient]);

  // Function to broadcast invalidation to other tabs
  const broadcastInvalidation = useCallback((): void => {
    try {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channel.postMessage({
        type: "INVALIDATE_QUERY",
        queryKey: queryKeyRef.current,
        timestamp: Date.now(),
      });
      channel.close();
    } catch (error) {
      logError({
        error,
        impact: "Failed to broadcast query invalidation to other tabs",
        extra: { queryKey: queryKeyRef.current },
      });
    }
  }, []);

  return { broadcastInvalidation };
}
